import React, { useState, useEffect, useRef, useCallback } from "react";
import useKeypress from "../hooks/useKeypress";
import useOnClickOutside from "../hooks/useOnClickOutside";
import DOMPurify from "dompurify";

import "./InlineEdit.scss";
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';

interface EditProps {
  width?: number;
  height?: number;
  text: string;
  onSetText?: any;
  onDiscard?: any;
  showSaving?: boolean;
  placeHolder?: string;
  focused?: boolean;
  canBeEmpty?: boolean;
}

const InlineEdit = (props: EditProps) => {


  const [isInputActive, setIsInputActive] = useState(props.focused || false);
  const [inputValue, setInputValue] = useState(props.text);

  const [savedTimer, setSavedTimer] = useState(false);

  const wrapperRef = useRef(null);
  const textRef = useRef(null);
  const inputRef: any = useRef(null);

  const { onSetText } = props;

  let width = props.width ?  props.width + 'px' : '302px';
  let height = props.height ? props.height + 'px' : '15px';

  const esc = useKeypress("Escape");

  useEffect(() => {
    setInputValue(props.text);
  }, [props.text, setInputValue]);

  const showSavedTimer = useCallback(() => {
    setSavedTimer(true);
    setTimeout(() => {
      setSavedTimer(false);
    }, 2000);
  }, [setSavedTimer]);

  const saveText = useCallback(() => {
    if (inputValue.length > 0 || props.canBeEmpty) {

      onSetText(inputValue);
      setIsInputActive(false);

      if (inputValue && inputValue.length > 0 && props.showSaving) {
        showSavedTimer();
      }
    } else {
      // do not accept empty fields
      onEsc();
    }
  }, [onSetText, setIsInputActive, inputValue, props.showSaving]);

  // check to see if the user clicked outside of this component
  useOnClickOutside(wrapperRef, () => {
    if (isInputActive) {
      saveText();
    }
  });

  const onEnter = useCallback(() => {
    saveText();
  }, [inputValue, onSetText]);

  const onEsc = useCallback(() => {
    setInputValue(props.text);
    setIsInputActive(false);
    if (props.onDiscard) {
      props.onDiscard();
    }
  }, [props.text]);

  // focus the cursor in the input field on edit start
  useEffect(() => {
    if (isInputActive) {
      if (inputRef && inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }
  }, [isInputActive]);

  const inputKeyPressed = useCallback((e) => {
    if (e.code === 'Enter') {
      onEnter();
    }
  }, [inputValue]);

  useEffect(() => {
    if (esc && isInputActive) {
      onEsc();
    }
  }, [esc, isInputActive]);

  const handleInputChange = useCallback(
    event => {
      // sanitize the input a little
      setInputValue(DOMPurify.sanitize(event.target.value));
  }, [setInputValue]);

  const handleTextClick = useCallback(() => {
    setSavedTimer(false);
    setIsInputActive(true);
  }, [setIsInputActive]);

  useEffect(() => {
    if (typeof props.focused !== 'undefined') {
      setIsInputActive(props.focused);
    }
  }, [props.focused])


  return (
    <div className="inline-text" ref={wrapperRef}>
      <div
        ref={textRef}
        style={{ maxWidth: width, height: height}}
        onClick={handleTextClick}
        className={`inline-text_copy inline-text_copy--${!isInputActive ? (savedTimer ? "saved" : "active") : "hidden"
          }`}
      >

        { // display text if length>0
          inputValue && inputValue.length > 0 && inputValue}
        { // else show placeholder
          (!inputValue || inputValue.length === 0) &&
          <div className="inline-placeholder">{props.placeHolder}</div>
        }
      </div>
      {
        savedTimer && <div className="inline-text_save-helper">
          <CheckCircleOutlineRoundedIcon style={{ marginTop: '-2px' }}></CheckCircleOutlineRoundedIcon>
          Saved!
        </div>
      }

      <input
        ref={inputRef}
        // set the width to the input length multiplied by the x height
        // it's not quite right but gets it close
        style={{ width: width, height: height}}
        value={inputValue}
        onChange={handleInputChange}
        onKeyPress={e => inputKeyPressed(e)}
        className={`inline-text_input inline-text_input--${isInputActive ? "active" : "hidden"}`}
      />
    </div>
  );
}

export default InlineEdit;
